import config from 'Cloud/config';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SharingNewWeblinkTab } from 'reactApp/components/SharingWindow/blocks/Weblink/SharingNewWeblinkTab';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { SharingContentFolders } from 'reactApp/components/SharingWindow/content/SharingContentFolders/SharingContentFolders';
import { type SendSharingContentAnalytics, type SharingContentProps, EFrom } from 'reactApp/components/SharingWindow/Sharing.types';
import { useIsWindowIntegrationSmall } from 'reactApp/hooks/useIsWindowIntegrationSmall';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { isOwnPublic as isOwnPublicSelector } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getItemById, getStorageItemById, isMountedOrSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { weblinkAccessControlLoadStart } from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.actions';
import type { RootState } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './SharingContentFoldersBiz.css';

const enum VIEW {
    options = 'options',
    personalAccess = 'personalAccess',
}

export const SharingContentFoldersBiz = memo(
    // eslint-disable-next-line max-lines-per-function
    (props: IPropsWithPopup<SharingContentProps>) => {
        const { itemStorage, id, from, isPhone, withPromoTooltip, gaSuffix, onboardingEnabled, onClose, fromEditor, variant } = props;
        const getItem = useCallback(
            (state) => (itemStorage ? getStorageItemById(state, itemStorage, id) : getItemById(state, id)),
            [id, itemStorage]
        );

        const item = useSelector(getItem) as PublishItem;
        const isPublic = !!config.get('PUBLIC');

        const [view, setView] = useState(() => {
            if (props.from === EFrom.SHARING && props.variant === SHARING_VARIANT.foldersBiz) {
                return VIEW.personalAccess;
            }
            return VIEW.options;
        });

        const refRoot = useRef<HTMLDivElement | null>(null);

        const sendSharingContentAnalytics = useCallback<SendSharingContentAnalytics>(({ action, label }) => {
            sendPaymentGa({ eventCategory: 'enable-sharing', action, label });
        }, []);

        useEffect(() => {
            sendSharingContentAnalytics({ action: 'show' });
        }, []);

        const isFolder = item?.isFolder;
        const isSharedOrMouted = useSelector((state: RootState) =>
            isFolder ? isMountedOrSharedFolder(state, item) : ({} as ReturnType<typeof isMountedOrSharedFolder>)
        );
        const storage = useSelector(getCurrentStorage);
        const { isEmbedded, isStock } = getStorage(storage);

        const isOwn = useSelector((state: RootState) => isPublic && isOwnPublicSelector(state));
        const noEdit = isStock ? false : isPublic && !isOwn;

        const dispatch = useDispatch();
        const isWindowSmall = useIsWindowIntegrationSmall();
        const isOwnPublic = useSelector((state: RootState) => isPublic && isOwnPublicSelector(state));

        const isBizUser = useSelector(UserSelectors.isBizUser);

        const itemName = useMemo(() => {
            if (!item?.name) {
                return null;
            }

            if (item.name.length > 102) {
                return `${item.name.slice(0, 50)}...${item.name.substring(item.name.length - 50, item.name.length)}`;
            }

            return item.name;
        }, [item?.name]);

        // tempexp_17779-start

        const onCloseAccess = useCallback(() => {
            if (storage === EStorageType.sharedLinks) {
                onClose();
            } else if (view === VIEW.options) {
                setView(VIEW.personalAccess);
            }
        }, [storage, view, onClose]);
        // tempexp_17779-end

        useEffect(() => {
            const xrayUserType = isBizUser ? 'biz' : 'b2c';

            sendXray(['sharing-content-redesign-mount', xrayUserType]);

            dispatch(weblinkAccessControlLoadStart({ id: item?.id || '', storage: item?.storage }));
        }, []);

        const viewComponent = useCallback(
            (view) => {
                switch (view) {
                    case VIEW.personalAccess:
                        return <SharingContentFolders {...{ ...props, onBackClick: () => setView(VIEW.options) }} />;
                    case VIEW.options:
                    default:
                        return (
                            <SharingNewWeblinkTab
                                id={id}
                                item={item}
                                from={from}
                                isSharedOrMouted={isSharedOrMouted}
                                gaSuffix={gaSuffix}
                                noEdit={noEdit}
                                isOwn={!isPublic || isOwnPublic}
                                isPublic={isPublic}
                                isPhone={isPhone}
                                onUnpublish={onCloseAccess}
                                onCloseModal={onClose}
                                sendSharingContentAnalytics={sendSharingContentAnalytics}
                                withPromoTooltip={withPromoTooltip}
                                openPersonalAccess={() => setView(VIEW.personalAccess)}
                                variant={variant}
                                isEmbedded={isEmbedded}
                                onboardingEnabled={onboardingEnabled}
                                foldSettings={isBizUser}
                                fromEditor={fromEditor}
                                formProps={props}
                            />
                        );
                }
            },
            [
                props,
                id,
                item,
                from,
                isSharedOrMouted,
                gaSuffix,
                noEdit,
                isPublic,
                isOwnPublic,
                isPhone,
                onCloseAccess,
                onClose,
                sendSharingContentAnalytics,
                withPromoTooltip,
                variant,
                isEmbedded,
                onboardingEnabled,
                isBizUser,
                fromEditor,
            ]
        );

        return (
            <div className={classNames(styles.root, { [styles.root_phone]: isPhone })} ref={refRoot}>
                {view !== VIEW.personalAccess && (
                    <div data-qa-id="sharing-header" className={classNames(styles.header)}>
                        Доступ к {variant === SHARING_VARIANT.foldersBiz ? 'папке' : 'файлу'} {itemName && <>&laquo;{itemName}&raquo;</>}
                    </div>
                )}

                <div className={classNames(styles.root, { [styles.root_phone]: isPhone, [styles.root_tutoria]: isWindowSmall })}>
                    {viewComponent(view)}
                </div>
            </div>
        );
    }
);

SharingContentFoldersBiz.displayName = 'SharingContentFoldersBiz';
